import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Check, Store } from 'lucide-react';
import clsx from 'clsx';
import { StepIndicator } from '../../../../../components/ui/StepIndicator';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { createSeller } from '../../../../../features/sellers/sellersThunks';
import { clearSellerCreation } from '../../../../../features/sellers/sellersSlice';
import { fetchAdminZoneOptions } from '../../../../../features/zones/zonesThunks';
import { fetchAdminOptions } from '../../../../../features/admins/adminsThunks';
import { selectCountryIso2 } from '../../../../../features/auth/authSelectors';
import { COUNTRY_ISO2 } from '../../../../../features/geo/geoConstants';
import {
  SELLER_STEPS,
  SUMMARY_STEP_INDEX,
  MAX_WAREHOUSES,
  createBlankSellerForm,
  createBlankWarehouse,
} from '../../../../../features/sellers/sellerFormConstants';
import {
  validateCompanyStep,
  validateAccountStep,
  validateAddressStep,
  validateWarehousesStep,
  validateAllSteps,
  isAccountStepBlocked,
  isValid,
  buildCreateSellerPayload,
} from '../../../../../features/sellers/sellerFormUtils';
import { CompanyStep } from './steps/CompanyStep';
import { AccountStep } from './steps/AccountStep';
import { AddressStep } from './steps/AddressStep';
import { WarehousesStep } from './steps/WarehousesStep';
import { SummaryStep } from './steps/SummaryStep';
import { SellerCreatedPanel } from './components/SellerCreatedPanel';

const BLANK_STEP_ERRORS = { company: {}, account: {}, address: {}, warehouses: {} };

const STEP_SUBTITLES = {
  company: 'Los datos de la empresa que vas a facturar.',
  account: 'El correo con el que el seller entrará a la plataforma.',
  address: 'La dirección comercial de la empresa.',
  warehouses: 'Desde dónde despacha. Puedes cargar hasta 10.',
  summary: 'Revisa antes de crear: esto abre una cuenta y le envía sus credenciales.',
};

/**
 * Seller creation, split into steps.
 *
 * The whole form lives in one `useState` here and the steps are presentational,
 * which is what lets the current step be the only one mounted: nothing is lost
 * on the way back, and the ten warehouse cards are only ever built once.
 *
 * Advancing validates the step being left; going back never validates. So a
 * mistake surfaces next to the field that caused it, on the screen where it was
 * made — the legacy form validated everything at the end and reported it as one
 * anonymous "complete los campos obligatorios".
 */
export const CreateSeller = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { creating, createError, createdSeller, emailCheck } = useSelector(
    (state) => state.sellers
  );
  const { adminZoneOptions, loadingAdminZoneOptions } = useSelector((state) => state.zones);

  const country = useSelector(selectCountryIso2);
  const isMexico = country === COUNTRY_ISO2.MX;

  /**
   * Whose seller this is going to be.
   *
   * The internal team opens accounts on behalf of an operator, so the owner is
   * a question rather than the account doing the typing — and the zones each
   * warehouse chooses from are that operator's, not this one's.
   */
  const adminOptions = useSelector((state) => state.admins.options);
  const [adminId, setAdminId] = useState('');

  const [form, setForm] = useState(() => createBlankSellerForm(country));
  const [stepErrors, setStepErrors] = useState(BLANK_STEP_ERRORS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visitedIds, setVisitedIds] = useState([SELLER_STEPS[0].id]);
  const [isGoingBack, setIsGoingBack] = useState(false);

  const currentStep = SELLER_STEPS[currentIndex];

  useEffect(() => {
    if (adminId) dispatch(fetchAdminZoneOptions(adminId));
  }, [adminId, dispatch]);

  useEffect(() => {
    dispatch(fetchAdminOptions());
  }, [dispatch]);

  // Leaving the page drops the result and the email lookup, so coming back
  // starts on a blank sheet instead of on the previous seller's credentials.
  useEffect(() => () => dispatch(clearSellerCreation()), [dispatch]);

  const invalidIds = useMemo(
    () => SELLER_STEPS.filter((step) => !isValid(stepErrors[step.id])).map((step) => step.id),
    [stepErrors]
  );

  /* ------------------------------ field changes ----------------------------- */

  /** Drops the errors of the fields just edited, so red clears as it is fixed. */
  const clearErrors = (stepId, keys) => {
    setStepErrors((previous) => {
      const stepMap = previous[stepId];
      if (!stepMap || keys.every((key) => !stepMap[key])) return previous;

      const next = { ...stepMap };
      keys.forEach((key) => delete next[key]);
      return { ...previous, [stepId]: next };
    });
  };

  const setField = (name, value) => {
    setForm((previous) => ({ ...previous, [name]: value }));
    clearErrors(currentStep.id, [name]);
  };

  const handleAddressChange = (patch) => {
    setForm((previous) => ({ ...previous, ...patch }));
    clearErrors('address', Object.keys(patch));
  };

  const handleWarehouseChange = (id, patch) => {
    setForm((previous) => ({
      ...previous,
      warehouses: previous.warehouses.map((warehouse) =>
        warehouse.id === id ? { ...warehouse, ...patch } : warehouse
      ),
    }));

    setStepErrors((previous) => {
      const list = previous.warehouses?.warehouses;
      if (!Array.isArray(list)) return previous;

      const index = form.warehouses.findIndex((warehouse) => warehouse.id === id);
      if (index < 0 || !list[index]) return previous;

      const entry = { ...list[index] };
      Object.keys(patch).forEach((key) => delete entry[key]);

      const nextList = [...list];
      nextList[index] = entry;
      return { ...previous, warehouses: { warehouses: nextList } };
    });
  };

  const handleAddWarehouse = () => {
    if (form.warehouses.length >= MAX_WAREHOUSES) return;
    setForm((previous) => ({
      ...previous,
      warehouses: [...previous.warehouses, createBlankWarehouse(country)],
    }));
  };

  const handleRemoveWarehouse = (id) => {
    setForm((previous) => ({
      ...previous,
      warehouses: previous.warehouses.filter((warehouse) => warehouse.id !== id),
    }));
    // The error list is positional, so it no longer lines up with the cards.
    setStepErrors((previous) => ({ ...previous, warehouses: {} }));
  };

  /* -------------------------------- navigation ------------------------------ */

  const validateStep = (stepId) => {
    switch (stepId) {
      case 'company':
        return validateCompanyStep(form);
      case 'account':
        return validateAccountStep(form, emailCheck);
      case 'address':
        return validateAddressStep(form, { isMexico });
      case 'warehouses':
        return validateWarehousesStep(form, { isMexico });
      default:
        return {};
    }
  };

  const goTo = (index) => {
    setIsGoingBack(index < currentIndex);
    setCurrentIndex(index);
    setVisitedIds((previous) => {
      const id = SELLER_STEPS[index].id;
      return previous.includes(id) ? previous : [...previous, id];
    });
  };

  const handleNext = () => {
    const errors = validateStep(currentStep.id);
    setStepErrors((previous) => ({ ...previous, [currentStep.id]: errors }));
    if (!isValid(errors)) return;

    const nextIndex = currentIndex + 1;

    // The summary is only honest if every step has been re-checked: a field can
    // have been broken on the way back after its own step was already passed.
    if (SELLER_STEPS[nextIndex].id === 'summary') {
      setStepErrors(validateAllSteps(form, { emailCheck, isMexico }));
    }

    goTo(nextIndex);
  };

  const handleStepClick = (index) => {
    // Jumping out of a step still records what was wrong with it, so the
    // indicator can mark it — but it never blocks the jump.
    if (currentStep.id !== 'summary') {
      const errors = validateStep(currentStep.id);
      setStepErrors((previous) => ({ ...previous, [currentStep.id]: errors }));
    }
    goTo(index);
  };

  const handleEditStep = (stepId) => {
    goTo(SELLER_STEPS.findIndex((step) => step.id === stepId));
  };

  /* --------------------------------- submit --------------------------------- */

  const handleSubmit = () => {
    const errors = validateAllSteps(form, { emailCheck, isMexico });
    setStepErrors(errors);
    if (!Object.values(errors).every(isValid)) return;

    dispatch(createSeller(buildCreateSellerPayload(form, { adminId })))
      .unwrap()
      // The rejection is already in the store and rendered as a banner; the
      // form is deliberately left untouched so the operator can fix and retry.
      .catch(() => {});
  };

  const handleCreateAnother = () => {
    dispatch(clearSellerCreation());
    setForm(createBlankSellerForm(country));
    setStepErrors(BLANK_STEP_ERRORS);
    setVisitedIds([SELLER_STEPS[0].id]);
    setIsGoingBack(false);
    setCurrentIndex(0);
  };

  /* --------------------------------- render --------------------------------- */

  if (createdSeller) {
    return (
      <SellerCreatedPanel
        result={createdSeller}
        onViewSeller={() => navigate(`/sellers/${createdSeller.company_id}`)}
        onCreateAnother={handleCreateAnother}
      />
    );
  }

  const isSummary = currentStep.id === 'summary';
  const isBlockedOnEmail = currentStep.id === 'account' && isAccountStepBlocked(form, emailCheck);
  const hasBlockingErrors = isSummary && invalidIds.length > 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-300">
      <div>
        <button
          type="button"
          onClick={() => navigate('/sellers')}
          className="flex items-center gap-1.5 text-sm font-semibold text-text-secondary hover:text-ninja-dark dark:hover:text-white transition-colors mb-4"
        >
          <ArrowLeft size={16} />
          Volver a sellers
        </button>

        <h1 className="text-2xl font-black text-ninja-dark dark:text-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400">
            <Store size={20} />
          </div>
          Crear Seller
        </h1>
      </div>

      {/* First, because everything under it depends on the answer: the zones a
          warehouse can pick are the operator's. */}
      <div
        data-testid="create-seller-operator"
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border p-5 sm:p-6"
      >
        <FilterableSelect
          variant="form"
          label="Operador logístico"
          value={adminId}
          onChange={setAdminId}
          options={adminOptions}
          placeholder="Seleccionar operador..."
          searchPlaceholder="Buscar operador..."
        />
        <p className="text-sm text-text-secondary mt-2">
          El seller queda bajo este operador, y sus bodegas eligen entre las zonas de despacho de
          ese operador.
        </p>
      </div>

      {/* `pointer-events-none` stops a click and nothing else: the fields stay
          writable by script, so a form that cannot be clicked can still be
          filled and sent. A disabled fieldset is the block that actually holds
          — for the keyboard, for a screen reader and for the end-to-end suite. */}
      <fieldset
        disabled={!adminId}
        aria-disabled={!adminId}
        data-testid="create-seller-steps"
        className={clsx(
          'bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden',
          !adminId && 'opacity-60'
        )}
      >
        <div className="px-6 sm:px-8 pt-6 pb-5 border-b border-border">
          <StepIndicator
            steps={SELLER_STEPS}
            currentIndex={currentIndex}
            visitedIds={visitedIds}
            invalidIds={invalidIds}
            onStepClick={handleStepClick}
          />
        </div>

        <div className="px-6 sm:px-8 py-6">
          <div
            key={currentStep.id}
            className={clsx(
              'animate-in fade-in duration-200',
              isGoingBack ? 'slide-in-from-left-4' : 'slide-in-from-right-4'
            )}
          >
            <div className="mb-6">
              <h2 className="text-lg font-bold text-ninja-dark dark:text-white">
                {currentStep.label}
              </h2>
              <p className="text-sm text-text-secondary mt-0.5">
                {STEP_SUBTITLES[currentStep.id]}
              </p>
            </div>

            {currentStep.id === 'company' && (
              <CompanyStep
                form={form}
                errors={stepErrors.company}
                onChange={setField}
                country={country}
              />
            )}

            {currentStep.id === 'account' && (
              <AccountStep
                form={form}
                errors={stepErrors.account}
                onChange={setField}
                emailCheck={emailCheck}
              />
            )}

            {currentStep.id === 'address' && (
              <AddressStep
                country={country}
                form={form}
                onChange={handleAddressChange}
                errors={stepErrors.address}
              />
            )}

            {currentStep.id === 'warehouses' && (
              <WarehousesStep
                warehouses={form.warehouses}
                errors={stepErrors.warehouses?.warehouses || []}
                country={country}
                zoneOptions={adminZoneOptions}
                loadingZones={loadingAdminZoneOptions}
                onWarehouseChange={handleWarehouseChange}
                onAddWarehouse={handleAddWarehouse}
                onRemoveWarehouse={handleRemoveWarehouse}
              />
            )}

            {isSummary && (
              <SummaryStep
                form={form}
                country={country}
                zoneOptions={adminZoneOptions}
                stepErrors={stepErrors}
                onEditStep={handleEditStep}
              />
            )}
          </div>

          {hasBlockingErrors && (
            <div className="mt-6 bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm">
              Hay campos incompletos. Revisa las etapas marcadas en rojo antes de crear el seller.
            </div>
          )}

          {createError && (
            <div className="mt-6 bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm">
              {createError}
            </div>
          )}
        </div>

        <div className="px-6 sm:px-8 py-5 border-t border-border flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/sellers')}
            disabled={creating}
            className="px-5 py-2.5 rounded-xl border border-border text-text-secondary hover:text-ninja-dark dark:hover:text-white hover:border-ninja-dark dark:hover:border-white font-semibold text-sm transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>

          <div className="flex items-center gap-3">
            {currentIndex > 0 && (
              <button
                type="button"
                onClick={() => goTo(currentIndex - 1)}
                disabled={creating}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl border border-border text-text-secondary hover:text-ninja-dark dark:hover:text-white hover:border-ninja-dark dark:hover:border-white font-semibold text-sm transition-colors disabled:opacity-50"
              >
                <ChevronLeft size={16} />
                Atrás
              </button>
            )}

            {isSummary ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={creating || hasBlockingErrors}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-ninja-green hover:bg-ninja-green/90 text-white font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                {creating ? 'Creando...' : 'Crear Seller'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={isBlockedOnEmail}
                className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl bg-ninja-green hover:bg-ninja-green/90 text-white font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continuar
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      </fieldset>
    </div>
  );
};
